import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/no-array-index-key */
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { Button, Icon, IconButton, MenuItem, Stack, Typography } from '@emburse/embark-core';
import { DeleteIcon } from '@emburse/embark-icons';
import { useForm, FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import ForteApplicationFormRow from '../Common/ForteApplicationFormRow';
import { useTranslation, $t } from '../../../hooks/useTranslation';
import { ApplicantOrganizationBusinessOwnershipType, OwnerTitle } from '../../../api/forteApplication';
import { ForteApplicationContext } from '../Common/ForteApplicationProvider';
import { FormAutocomplete, FormSelect, FormTextField } from '../../../components/FormComponents';
import FormDatePicker from '../../../components/FormComponents/FormDatePicker';
function OwnerAccountControllerInfoForm({ saveButtonText, backButtonText, afterFormSave, backButtonCallback }) {
    useTranslation();
    const { loading, error, currentApplication, saveApplication, statesAndProvinces, countries, owners } = useContext(ForteApplicationContext);
    const isSingleOwnerLegalStructure = useMemo(() => {
        if (!currentApplication) {
            return false;
        }
        return [
            ApplicantOrganizationBusinessOwnershipType.Government,
            ApplicantOrganizationBusinessOwnershipType.SoleProprietorship,
            ApplicantOrganizationBusinessOwnershipType.PubliclyHeldCorporation
        ].includes(currentApplication.applicant_organization.legal_structure);
    }, [currentApplication]);
    const ownerSchema = useMemo(() => z.object({
        first_name: z.string().min(1, { message: 'First name is required' }),
        last_name: z.string().min(1, { message: 'Last name is required' }),
        title: z.nativeEnum(OwnerTitle, { message: 'Invalid title' }),
        email_address: z.string().email(),
        mobile_phone: z.string().min(1, { message: 'Invalid phone number' }),
        percentage: z
            .string()
            .transform((value) => (isSingleOwnerLegalStructure ? '100' : value))
            .pipe(z
            .string()
            .min(1, { message: 'Percentage is required' })
            .regex(/^\d+(\.\d+)?$/, { message: 'Invalid percentage' })
            .refine((value) => Number(value) >= 0 && Number(value) <= 100, {
            message: 'Invalid percentage'
        })),
        street_address1: z.string().min(1, { message: 'Street address is required' }),
        postal_code: z.string().min(5, { message: 'Postal code is required' }),
        city: z.string().min(1, { message: 'City is required' }),
        state: z.object({
            Code: z.string().min(2, { message: 'State is required' }),
            Name: z.string().min(2, { message: 'State is required' })
        }, { message: 'State is required' }),
        date_of_birth: z
            .instanceof(dayjs)
            .refine((value) => !Number.isNaN(value.date()), {
            message: 'Invalid date of birth'
        }),
        last4_ssn: z.string().regex(/^\d{4}$/, { message: 'Invalid SSN' }),
        countryOfCitizenship: z.object({
            IsoCode: z
                .string()
                .min(2, { message: 'Country of citizenship is required' }),
            Name: z.string().min(2, { message: 'Country of citizenship is required' })
        }, { message: 'Country of citizenship is required' }),
        country: z.string()
    }), [isSingleOwnerLegalStructure]);
    const formSchema = useMemo(() => z
        .object({
        owners: z
            .array(ownerSchema)
            .min(1, { message: 'At least one owner is required' })
    })
        .superRefine((data, ctx) => {
        const { owners: currentFormOwners } = data;
        // Validate individual ownership percentages based on index
        currentFormOwners.forEach((owner, index) => {
            if (index === 0) {
                const percentage = Number(owner.percentage);
                if (percentage < 0 || percentage > 100) {
                    ctx.addIssue({
                        path: ['owners', index, 'percentage'],
                        message: 'Owner/Account controller percentage must be between 0 and 100',
                        code: z.ZodIssueCode.custom
                    });
                }
            }
            else {
                const percentage = Number(owner.percentage);
                if (percentage < 25 || percentage > 100) {
                    ctx.addIssue({
                        path: ['owners', index, 'percentage'],
                        message: 'Additional owners must have a percentage between 25 and 100',
                        code: z.ZodIssueCode.custom
                    });
                }
            }
        });
        // Validate total ownership percentage
        const totalPercentage = currentFormOwners.reduce((sum, owner) => sum + Number(owner.percentage), 0);
        if (totalPercentage > 100) {
            ctx.addIssue({
                path: ['owners'],
                message: 'Total ownership percentage cannot exceed 100%',
                code: z.ZodIssueCode.custom
            });
        }
        // Validate the number of owners against the legal structure
        if (!currentApplication) {
            return;
        }
        if (isSingleOwnerLegalStructure && owners.length > 1) {
            ctx.addIssue({
                path: ['owners'],
                message: 'A maximum of 1 owner is allowed for the selected legal structure',
                code: z.ZodIssueCode.custom
            });
        }
    }), [currentApplication, isSingleOwnerLegalStructure, ownerSchema, owners.length]);
    const defaultOwner = useMemo(() => ({
        first_name: '',
        last_name: '',
        title: OwnerTitle.CEO,
        email_address: '',
        mobile_phone: '',
        percentage: '',
        street_address1: '',
        postal_code: '',
        city: '',
        state: loading ? { Code: '', Name: '' } : statesAndProvinces[0],
        date_of_birth: dayjs('1/1/2000'),
        last4_ssn: '0000',
        countryOfCitizenship: loading
            ? { IsoCode: 'USA', Name: 'United States of America' }
            : countries.find((option) => option.IsoCode === 'USA'),
        country: ''
    }), [loading, statesAndProvinces, countries]);
    const formMethods = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            owners: [defaultOwner]
        }
    });
    useEffect(() => {
        if (loading || error || !currentApplication) {
            return;
        }
        const formData = {
            owners: new Array(owners.length).fill({})
        };
        owners.forEach((owner, index) => {
            formData.owners[index] = {
                first_name: owner.first_name || '',
                last_name: owner.last_name || '',
                title: owner.title || OwnerTitle.CEO,
                email_address: owner.email_address || '',
                mobile_phone: owner.mobile_phone || '',
                percentage: owner.percentage ? owner.percentage.toString() : '',
                street_address1: owner.street_address1 || '',
                postal_code: owner.postal_code || '',
                city: owner.locality || '',
                state: owner.region
                    ? statesAndProvinces.find((option) => option.Code === owner.region)
                    : statesAndProvinces[0],
                date_of_birth: owner.date_of_birth.toString() !== '0001-01-01T00:00:00'
                    ? dayjs(owner.date_of_birth)
                    : dayjs('1/1/2000'),
                last4_ssn: owner.last4_ssn || '0000',
                countryOfCitizenship: owner.citizenship
                    ? countries.find((option) => option.IsoCode === owner.citizenship)
                    : countries.find((option) => option.IsoCode === 'USA'),
                country: owner.country || ''
            };
        });
        formMethods.reset(formData);
    }, [currentApplication, statesAndProvinces, countries, owners, loading, error, formMethods]);
    const onSubmit = useCallback(async (ownersData) => {
        const newOwners = ownersData.owners.map((ownerData) => ({
            first_name: ownerData.first_name,
            last_name: ownerData.last_name,
            title: ownerData.title,
            email_address: ownerData.email_address,
            mobile_phone: ownerData.mobile_phone,
            percentage: Number(ownerData.percentage),
            street_address1: ownerData.street_address1,
            locality: ownerData.city,
            region: statesAndProvinces.find((option) => option.Code === ownerData.state.Code)
                .Code,
            postal_code: ownerData.postal_code,
            country: countries.find((option) => option.IsoCode === ownerData.countryOfCitizenship.IsoCode).IsoCode,
            citizenship: countries.find((option) => option.IsoCode === ownerData.countryOfCitizenship.IsoCode).IsoCode,
            date_of_birth: ownerData.date_of_birth.toDate(),
            last4_ssn: ownerData.last4_ssn
        }));
        await saveApplication({
            ...currentApplication,
            owner: newOwners[0],
            owner_2: newOwners[1],
            owner_3: newOwners[2],
            owner_4: newOwners[3]
        });
        afterFormSave?.();
    }, [saveApplication, currentApplication, afterFormSave, statesAndProvinces, countries]);
    const handleAddOwner = useCallback(async () => {
        const newOwnerDto = {
            first_name: '',
            last_name: '',
            title: OwnerTitle.CEO,
            email_address: '',
            mobile_phone: '',
            percentage: 0,
            street_address1: '',
            locality: '',
            region: statesAndProvinces[0].Code,
            postal_code: '',
            country: '',
            citizenship: 'USA',
            date_of_birth: new Date('1/1/2000'),
            last4_ssn: '0000'
        };
        const newCurrentOwners = formMethods.getValues().owners.map((ownerData) => ({
            first_name: ownerData.first_name,
            last_name: ownerData.last_name,
            title: ownerData.title,
            email_address: ownerData.email_address,
            mobile_phone: ownerData.mobile_phone,
            percentage: Number(ownerData.percentage),
            street_address1: ownerData.street_address1,
            locality: ownerData.city,
            region: statesAndProvinces.find((option) => option.Code === ownerData.state.Code).Code,
            postal_code: ownerData.postal_code,
            country: countries.find((option) => option.IsoCode === ownerData.countryOfCitizenship.IsoCode).IsoCode,
            citizenship: countries.find((option) => option.IsoCode === ownerData.countryOfCitizenship.IsoCode).IsoCode,
            date_of_birth: ownerData.date_of_birth.toDate(),
            last4_ssn: ownerData.last4_ssn
        }));
        const newOwners = [...newCurrentOwners, newOwnerDto];
        await saveApplication({
            ...currentApplication,
            owner: newOwners[0],
            owner_2: newOwners[1],
            owner_3: newOwners[2],
            owner_4: newOwners[3]
        });
    }, [currentApplication, countries, formMethods, saveApplication, statesAndProvinces]);
    return (_jsxs(Stack, { alignItems: "flex-start", sx: { width: '100%' }, spacing: 4, children: [_jsx(FormProvider, { ...formMethods, children: formMethods.getValues().owners.map((_, index) => (_jsxs(Stack, { alignItems: "flex-start", sx: { width: '100%' }, spacing: 3, children: [_jsxs(Stack, { direction: "row", spacing: 2, sx: { width: '100%' }, justifyContent: "flex-end", alignItems: "center", children: [_jsxs(Stack, { direction: "column", spacing: 0, alignItems: "flex-start", sx: { width: '100%' }, children: [_jsx(Typography, { variant: "h6", sx: { flexGrow: 1 }, children: index === 0
                                                ? $t('Owner/Account Controller Information')
                                                : $t('Additional Owner Information') }), index === 0 ? (_jsx(Typography, { variant: "body2", color: "textSecondary", sx: { whiteSpace: 'pre-line' }, children: $t(`Enter details for the Account Controller from your organization. This individual will serve as the main point of contact and should be someone with significant responsibility for managing the business, such as an owner, executive officer, or senior manager.${!isSingleOwnerLegalStructure
                                                ? `\n\nAdditionally, click "Add Owner" to list any owners who have 25% or more direct or indirect ownership of the business.`
                                                : ''}`) })) : (_jsx(Typography, { variant: "body2", color: "textSecondary", children: $t(`Enter details for any individuals who have 25% or more direct or indirect ownership of the business`) }))] }), index > 0 && (_jsx(IconButton, { color: "error", onClick: async () => {
                                        const newOwners = [...owners];
                                        newOwners.splice(index, 1);
                                        await saveApplication({
                                            ...currentApplication,
                                            owner: newOwners[0],
                                            owner_2: newOwners[1],
                                            owner_3: newOwners[2],
                                            owner_4: newOwners[3]
                                        });
                                    }, children: _jsx(Icon, { iconUrl: DeleteIcon }) }))] }), _jsxs(ForteApplicationFormRow, { bottomSpacer: !(formMethods.formState.errors.owners?.[index]?.first_name ||
                                formMethods.formState.errors.owners?.[index]?.last_name), children: [_jsx(FormTextField, { name: `owners.${index}.first_name`, label: $t('First Name'), id: `owner-account-controller-info-form-first-name-${index}`, fullWidth: true }), _jsx(FormTextField, { name: `owners.${index}.last_name`, label: "Last Name", id: `owner-account-controller-info-form-last-name-${index}`, fullWidth: true }), _jsxs(FormSelect, { name: `owners.${index}.title`, label: $t('Title'), id: `owner-account-controller-info-form-title-${index}`, fullWidth: true, children: [_jsx(MenuItem, { value: OwnerTitle.CEO, children: "CEO" }), _jsx(MenuItem, { value: OwnerTitle.CFO, children: "CFO" }), _jsx(MenuItem, { value: OwnerTitle.COO, children: "COO" }), _jsx(MenuItem, { value: OwnerTitle.President, children: "President" }), _jsx(MenuItem, { value: OwnerTitle.VicePresident, children: "Vice President" }), _jsx(MenuItem, { value: OwnerTitle.Treasurer, children: "Treasurer" }), _jsx(MenuItem, { value: OwnerTitle.ManagingMember, children: "Managing Member" }), _jsx(MenuItem, { value: OwnerTitle.GeneralPartner, children: "General Partner" }), _jsx(MenuItem, { value: OwnerTitle.Other, children: "Other" })] })] }), _jsxs(ForteApplicationFormRow, { bottomSpacer: !(formMethods.formState.errors.owners?.[index]?.email_address ||
                                formMethods.formState.errors.owners?.[index]?.mobile_phone ||
                                formMethods.formState.errors.owners?.[index]?.percentage), children: [_jsx(FormTextField, { name: `owners.${index}.email_address`, label: $t('Email'), id: `owner-account-controller-info-form-email-address-${index}`, fullWidth: true }), _jsx(FormTextField, { name: `owners.${index}.mobile_phone`, label: $t('Mobile Phone'), id: `owner-account-controller-info-form-mobile-phone-${index}`, fullWidth: true }), !isSingleOwnerLegalStructure && (_jsx(FormTextField, { name: `owners.${index}.percentage`, label: $t('Ownership Percentage'), id: `owner-account-controller-info-form-percentage-${index}`, fullWidth: true }))] }), _jsx(ForteApplicationFormRow, { bottomSpacer: !formMethods.formState.errors.owners?.[index]?.street_address1, children: _jsx(FormTextField, { name: `owners.${index}.street_address1`, label: $t('Home Address'), id: `owner-account-controller-info-form-street-address-${index}`, fullWidth: true }) }), _jsxs(ForteApplicationFormRow, { bottomSpacer: !(formMethods.formState.errors.owners?.[index]?.postal_code ||
                                formMethods.formState.errors.owners?.[index]?.city ||
                                formMethods.formState.errors.owners?.[index]?.state), children: [_jsx(FormTextField, { name: `owners.${index}.city`, label: $t('City'), id: `owner-account-controller-info-form-city-${index}`, fullWidth: true }), _jsx(FormAutocomplete, { name: `owners.${index}.state`, label: $t('State'), id: `owner-account-controller-info-form-state-${index}`, options: statesAndProvinces, isOptionEqualToValue: (option, value) => option.Code === value?.Code, getOptionLabel: (option) => option.Name, fullWidth: true }), _jsx(FormTextField, { name: `owners.${index}.postal_code`, label: $t('Zip Code'), id: `owner-account-controller-info-form-postal-code-${index}`, fullWidth: true })] }), _jsxs(ForteApplicationFormRow, { children: [_jsx(FormDatePicker, { name: `owners.${index}.date_of_birth`, label: $t('Date of Birth'), id: `owner-account-controller-info-form-date-of-birth-${index}`, helperText: "MM/DD/YYYY", fullWidth: true }), _jsx(FormAutocomplete, { name: `owners.${index}.countryOfCitizenship`, label: $t('Country of Citizenship'), id: `owner-account-controller-info-form-country-of-citizenship-${index}`, options: countries, isOptionEqualToValue: (option, value) => option.IsoCode === value?.IsoCode, getOptionLabel: (option) => option.Name, onChange: (_event, value) => {
                                        if (value?.IsoCode !== 'US') {
                                            formMethods.setValue(`owners.${index}.last4_ssn`, '0000');
                                        }
                                    }, fullWidth: true }), _jsx(FormTextField, { name: `owners.${index}.last4_ssn`, label: formMethods.watch(`owners.${index}.countryOfCitizenship`)
                                        ?.IsoCode === 'USA'
                                        ? $t('Last 4 SSN')
                                        : $t('Last 4 ITIN'), id: `owner-account-controller-info-form-last4-ssn-${index}`, helperText: formMethods.watch(`owners.${index}.countryOfCitizenship`)
                                        ?.IsoCode === 'USA'
                                        ? "Enter the last 4 digits of the individual's Social Security Number."
                                        : "Enter the last 4 digits of the owner's Individual Tax ID Number.", fullWidth: true })] })] }, index))) }), _jsxs(Stack, { spacing: 4, sx: { width: '100%' }, alignItems: "flex-start", children: [formMethods.formState.errors?.owners?.root?.message && (_jsx(Typography, { color: "error", children: formMethods.formState.errors.owners.root.message })), _jsxs(Stack, { direction: "row", justifyContent: "space-between", alignItems: "flex-end", sx: { width: '100%' }, children: [backButtonCallback !== undefined && (_jsx(Button, { variant: "outlined", onClick: backButtonCallback, children: backButtonText || $t('Back') })), _jsxs(Stack, { direction: "row", spacing: 2, children: [!isSingleOwnerLegalStructure && (_jsx(Button, { onClick: handleAddOwner, disabled: owners.length >= 4 || loading, variant: "outlined", children: $t('Add Owner') })), _jsx(Button, { onClick: formMethods.handleSubmit(onSubmit), children: saveButtonText || $t('Submit') })] })] })] })] }));
}
OwnerAccountControllerInfoForm.defaultProps = {
    saveButtonText: 'Submit',
    backButtonText: 'Back',
    afterFormSave: undefined,
    backButtonCallback: undefined
};
export default OwnerAccountControllerInfoForm;
